import { useNavigate } from '@tanstack/react-router';
import {
  CalendarCheck,
  ClipboardCheck,
  FileCheck2,
  Layers,
  Phone,
  Send,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { RoleKey } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { useCurrentSession } from '../../../stores/session';
import { useMarkWelcomeSeen } from '../api/auth-api';
import { AuthLayout } from '../components/AuthLayout';
import styles from './auth-forms.module.css';

/**
 * AUTH-09 — the first thing somebody sees, once.
 *
 * **Role-variant, and deliberately short.** A panelist signs in three times a
 * quarter to fill in a scorecard; a product tour is an insult to their time. They
 * get twenty seconds of "here is what you will be asked to do". A recruiter, who
 * lives in this thing, gets the four places they will spend their day.
 *
 * The content is per role rather than per screen count, because what somebody needs
 * to be told is a function of their job, not of how much of the app they can reach.
 */

interface WelcomeStep {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly body: string;
}

interface WelcomeContent {
  readonly title: string;
  readonly subtitle: string;
  readonly steps: readonly WelcomeStep[];
  readonly cta: string;
}

const CONTENT: Readonly<Record<RoleKey, WelcomeContent>> = {
  panelist: {
    title: 'Two things, and that is all',
    subtitle: 'You will be asked to do exactly this much. It takes about a minute each time.',
    steps: [
      {
        icon: ClipboardCheck,
        title: 'Score the people you interview',
        body: 'After each interview you get a short form — a rating against a few criteria, and a comment. That is the whole job.',
      },
      {
        icon: Users,
        title: 'Everything else is already set up',
        body: 'Times, rooms, CVs and questions arrive with the invitation. You never have to go looking for them.',
      },
    ],
    cta: 'Got it',
  },
  hiring_manager: {
    title: "Here's your part of hiring",
    subtitle: 'Three things come to you, and each one is a link in an email.',
    steps: [
      {
        icon: Send,
        title: 'Ask for a hire',
        body: 'Raise a requisition for a role you need. You can see where it is in the approval chain at any time.',
      },
      {
        icon: Users,
        title: 'Watch your shortlist',
        body: 'See who is in the pipeline for your roles, and how far along they are.',
      },
      {
        icon: FileCheck2,
        title: 'Decide after each round',
        body: 'Your interviewers score; you decide whether the person moves on.',
      },
    ],
    cta: 'Show me my roles',
  },
  approver: {
    title: 'Approvals come to you here',
    subtitle: 'You will be emailed a link each time. This is what is behind it.',
    steps: [
      {
        icon: FileCheck2,
        title: 'Approve, reject, or send back',
        body: 'Every request shows the role, the budget and who asked. Sending back asks for a change without rejecting outright.',
      },
      {
        icon: Sparkles,
        title: 'Going away? Name a delegate',
        body: 'Set who covers your approvals for a date range, so nothing waits on you while you are out.',
      },
    ],
    cta: "Let's go",
  },
  recruiter: {
    title: 'Welcome — this is where your day happens',
    subtitle: 'Four places, and you will live in the first two.',
    steps: [
      {
        icon: Layers,
        title: 'Sorting mode',
        body: 'One candidate at a time, keyboard-driven. It is the fastest way through a pile of applications.',
      },
      {
        icon: Users,
        title: 'The pipeline board',
        body: 'Every live candidate for a job, by stage. Drag to move, select many to act on many.',
      },
      {
        icon: Send,
        title: 'Jobs and postings',
        body: 'Build a job from a template, then publish it where you need it.',
      },
      {
        icon: ClipboardCheck,
        title: 'Your workspace',
        body: 'Everything waiting on you, in the order it needs doing.',
      },
    ],
    cta: 'Start',
  },
  admin: {
    title: 'You have the whole picture',
    subtitle: 'A few things worth knowing on day one.',
    steps: [
      {
        icon: Users,
        title: 'People and roles',
        body: 'Invite colleagues, give them a role and a scope, and make new roles, blank or from a standard one.',
      },
      {
        icon: Sparkles,
        title: 'View as somebody else',
        body: 'The fastest way to answer "why can’t they see this?". It is read-only, ends after 30 minutes, and is always logged.',
      },
      {
        icon: FileCheck2,
        title: 'The access log',
        body: 'Every sign-in, every refused action, every role change. Misconfigured roles show up here first.',
      },
    ],
    cta: 'Take me in',
  },
  hr_lead: {
    title: 'You run hiring here',
    subtitle: 'A few things worth knowing on day one.',
    steps: [
      {
        icon: FileCheck2,
        title: 'Check hire requests',
        body: 'Every request comes to HR first. Check it, fix anything unclear, then it goes to Leadership.',
      },
      {
        icon: Send,
        title: 'Jobs, from request to selected',
        body: 'An approved request becomes a draft job. Fill it, open it, then import, rank, review and shortlist.',
      },
      {
        icon: Layers,
        title: 'Hiring set-up',
        body: 'Reasons, message templates, pipelines, calendars, rooms and the staff list are under Admin.',
      },
    ],
    cta: 'Take me in',
  },
  leadership: {
    title: 'Approvals come to you here',
    subtitle: 'You give the final yes on hire requests, and you can look at every job.',
    steps: [
      {
        icon: FileCheck2,
        title: 'Approve, reject, or send back',
        body: 'HR has already checked each request. Approve it and the job is made at once.',
      },
      {
        icon: Users,
        title: 'See how hiring is going',
        body: 'Open any job, candidate or report. You can look at everything; nothing changes unless you approve.',
      },
    ],
    cta: "Let's go",
  },
  invigilator: {
    title: 'Here for the exam sessions',
    subtitle: 'One job, and the app stays out of your way.',
    steps: [
      {
        icon: ClipboardCheck,
        title: 'Run the session',
        body: 'Mark who turned up, start the exam, and close it when time is up.',
      },
    ],
    cta: 'Got it',
  },
  hr_assistant: {
    title: 'You help candidates get here',
    subtitle: 'Three things, and each starts from a job or the calendar.',
    steps: [
      {
        icon: Phone,
        title: 'Call the people on the contact list',
        body: 'Open a job and go to Contact. Tap a number to call, then mark how it went, or email them from a template.',
      },
      {
        icon: CalendarCheck,
        title: 'Know who is coming in today',
        body: 'The interview calendar shows who is due and when, so you can greet them and have the room ready.',
      },
      {
        icon: ClipboardCheck,
        title: 'Watch the tests taken here',
        body: 'When you are named on an exam session, mark who turned up, start the test and close it when time is up.',
      },
    ],
    cta: 'Got it',
  },
  candidate: {
    title: 'Welcome',
    subtitle: 'Everything you need arrives by email.',
    steps: [],
    cta: 'Continue',
  },
};

/** Somebody with two roles gets the fuller of the two briefings, not both. */
const DEPTH: readonly RoleKey[] = [
  'admin',
  'hr_lead',
  'leadership',
  'recruiter',
  'hiring_manager',
  'approver',
  'hr_assistant',
  'invigilator',
  'panelist',
  'candidate',
];

function contentFor(roles: readonly RoleKey[]): WelcomeContent {
  const primary = DEPTH.find((role) => roles.includes(role)) ?? 'panelist';
  return CONTENT[primary];
}

export function WelcomePage(): ReactNode {
  const navigate = useNavigate();
  const session = useCurrentSession();
  const markSeen = useMarkWelcomeSeen();

  const content = contentFor(session?.roles ?? []);
  const firstName = session?.fullName.split(' ')[0];

  return (
    <AuthLayout
      title={firstName ? `${content.title}, ${firstName}` : content.title}
      subtitle={content.subtitle}
      icon={<Sparkles size={20} aria-hidden />}
    >
      <ul className={styles.steps}>
        {content.steps.map((step) => (
          <li key={step.title} className={styles.step}>
            <span className={styles.stepMark} aria-hidden>
              <step.icon size={15} />
            </span>
            <div>
              <p className={styles.stepTitle}>{step.title}</p>
              <p className={styles.stepBody}>{step.body}</p>
            </div>
          </li>
        ))}
      </ul>

      <Button
        variant="solid"
        size="lg"
        block
        onClick={() => {
          // Marked seen and moved on together: waiting for the round trip would
          // make the one button on the screen feel slow (CLAUDE.md §5a rule 1).
          // `mutate` marks the cached session seen before it returns, which is
          // what lets the router's gate wave this navigation through.
          markSeen.mutate();
          void navigate({ to: '/' });
        }}
      >
        {content.cta}
      </Button>
    </AuthLayout>
  );
}
